import { useStyles } from 'utils/useStyles';
import { css } from '@emotion/react';
import { useState } from 'react';

type Star = {
  x: number;
  y: number;
  r: number;
  color: string;
  isPointyStar: boolean;
  speed: number;
};

const STARFIELD_WIDTH = 4000;
const STARFIELD_HEIGHT = 2500;

function mulberry32(a: number) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const random = mulberry32(109);

const AQUA = ['#4ECDC4', '#F474EB'];
const ORANGE = ['#FF8833', '#FFA044'];
const LAYERS = [
  { speedMin: 0.07, speedMax: 0.35, count: 125, circleR: [1.0, 2.0], starR: [2.5, 4.5], starRatio: 0.15 },
  { speedMin: 0.38, speedMax: 0.5, count: 20, circleR: [1.5, 3.0], starR: [4.0, 7.0], starRatio: 0.35 },
  { speedMin: 0.65, speedMax: 1.1, count: 25, circleR: [2.0, 3.5], starR: [18.0, 24.0], starRatio: 0.55 },
];

function rand(a: number, b: number) {
  return random() * (b - a) + a;
}

function choice(items: string[]) {
  return items[Math.floor(random() * items.length)];
}

function seedStars() {
  const stars: Star[] = [];
  LAYERS.forEach((layer, layerIndex) => {
    for (let i = 0; i < layer.count; i++) {
      const isPointyStar = random() < layer.starRatio;
      const color = isPointyStar ? choice(random() < 0.8 ? ORANGE : AQUA) : choice(random() < 0.5 ? AQUA : ORANGE);
      const [rMin, rMax] = isPointyStar ? layer.starR : layer.circleR;
      const r = rand(rMin, rMax);
      stars.push({
        x: rand(0, STARFIELD_WIDTH),
        y: rand(0, STARFIELD_HEIGHT),
        r,
        color: layerIndex === 0 ? color + '88' : color,
        isPointyStar,
        speed: rand(layer.speedMin, layer.speedMax),
      });
    }
  });
  return stars;
}

const Star = ({ star }: { star: Star }) => {
  const styles = useStyles(getStyles);

  return star.isPointyStar ? (
    <div css={[styles.pointyStar(star.color, star.r), styles.animateStar(star.x, star.y, star.speed)]}>
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0L14.2 9.8 24 12 14.2 14.2 12 24 9.8 14.2 0 12 9.8 9.8Z" fill="#FF8833" />
      </svg>
    </div>
  ) : (
    <div css={[styles.circleStar(star.color, star.r), styles.animateStar(star.x, star.y, star.speed)]}></div>
  );
};

const Starfield = () => {
  const styles = useStyles(getStyles);
  const [stars, _] = useState(seedStars);

  return (
    <div css={styles.starfield} aria-hidden="true">
      <div css={styles.sunGradient}></div>
      <div css={styles.stars}>
        {stars.map((star, i) => (
          <Star star={star} key={i} />
        ))}
      </div>
    </div>
  );
};

const initialStarAnimationDuration = (x: number, speed: number) => {
  return infiniteStarAnimationDuration(speed) * (x / STARFIELD_WIDTH);
};

const infiniteStarAnimationDuration = (speed: number) => {
  return STARFIELD_WIDTH / (speed * 100);
};

const animationKey = (x: number, speed: number) => `${Math.round(x)}_${Math.round(speed * 100)}`;

const getStyles = () => ({
  starfield: css`
    @property --start-color {
      syntax: '<color>';
      inherits: false;
      initial-value: #f4f5f7;
    }

    @property --mid-color {
      syntax: '<color>';
      inherits: false;
      initial-value: #f4f5f7;
    }

    @property --end-color {
      syntax: '<color>';
      inherits: false;
      initial-value: #f4f5f7;
    }

    position: fixed;
    width: 100%;
    height: 100%;
    background: rgb(11, 12, 15);
    background: linear-gradient(135deg, var(--start-color) 0%, var(--mid-color) 50%, var(--end-color) 100%);

    animation: space linear 3s 2.4s forwards;

    @keyframes space {
      0% {
        --start-color: #f4f5f7;
        --mid-color: #f4f5f7;
        --end-color: #f4f5f7;
      }

      50% {
        --start-color: #5fe2c0;
        --mid-color: #1a7aa8;
        --end-color: #0e2040;
      }

      100% {
        --start-color: #0d2025;
        --mid-color: #0b1018;
        --end-color: #0b0c0f;
      }
    }
  `,
  stars: css`
    position: absolute;
    width: ${STARFIELD_WIDTH}px;
    height: ${STARFIELD_HEIGHT}px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
  sunGradient: css`
    width: 100%;
    height: 100%;
    background: radial-gradient(
      2050px 2800px at -13% -17%,
      rgba(251, 202, 10, 0.4) 0%,
      rgba(247, 150, 30, 0.27) 14%,
      rgba(240, 90, 40, 0.16) 34%,
      rgba(170, 70, 28, 0.08) 54%,
      rgba(60, 30, 16, 0.03) 72%,
      rgba(11, 12, 15, 0) 90%
    );
    opacity: 0;

    animation: sun 3.5s 2.4s ease-in forwards;

    @keyframes sun {
      from {
        opacity: 0;
      }

      to {
        opacity: 0.8;
      }
    }
  `,
  animateStar: (x: number, y: number, speed: number) => css`
    position: absolute;
    left: 0px;
    top: 0px;
    transform: translateX(${x}px) translateY(${y}px);

    animation:
      moveStar_initial_${animationKey(x, speed)} ease-in ${initialStarAnimationDuration(x, speed)}s 3.5s,
      moveStar_infinite_${animationKey(x, speed)} linear ${infiniteStarAnimationDuration(speed)}s
        ${initialStarAnimationDuration(x, speed) + 3.5}s infinite;

    @keyframes moveStar_initial_${animationKey(x, speed)} {
      from {
        transform: translateX(${x}px) translateY(${y}px);
      }

      to {
        transform: translateX(0px) translateY(${y}px);
      }
    }

    @keyframes moveStar_infinite_${animationKey(x, speed)} {
      from {
        transform: translateX(${STARFIELD_WIDTH}px) translateY(${y}px);
      }

      to {
        transform: translateX(0px) translateY(${y}px);
      }
    }
  `,
  pointyStar: (color: string, radius: number) => css`
    transform: translateX(-${radius}px) translateY(-${radius}px); /* ensure that star goes fully off screen before reseting position */

    svg {
      width: ${radius}px;
      height: ${radius}px;
    }

    path {
      fill: ${color};
    }
  `,
  circleStar: (color: string, radius: number) => css`
    width: ${radius * 2}px;
    height: ${radius * 2}px;
    border-radius: ${radius}px;
    background: ${color};
  `,
});

export default Starfield;
